import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Campo de percentual com − e + (inspirado na tela de lance da Servopa), no
 * lugar dos sliders do Simulador. Só a entrada muda: o valor que sai daqui é o
 * mesmo número que o slider entregava, e a conta continua em simulacao.ts.
 *
 * Regras (pedido de 08/10/2026): aceita vírgula (48,5); − e + andam 0,5;
 * acima do máximo corrige para o máximo e avisa; abaixo de 0 vira 0; vazio ao
 * sair volta a 0; − desabilita em 0 e + no máximo.
 */

export const PASSO_PCT = 0.5;

/** "48,5" → 48.5; "" ou texto inválido → null. */
export const lerPercentual = (texto: string): number | null => {
  // Vírgula ou ponto como decimal: em percentual não há milhar.
  const limpo = texto.replace('%', '').trim().replace(',', '.');
  if (limpo === '' || limpo === '-') return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
};

export const formatarPercentual = (n: number): string =>
  n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

/** Dentro de 0..max; diz se precisou cortar no máximo. */
export const limitarPercentual = (n: number, max: number): { valor: number; acimaDoMaximo: boolean } => {
  if (n > max) return { valor: max, acimaDoMaximo: true };
  if (n < 0) return { valor: 0, acimaDoMaximo: false };
  return { valor: n, acimaDoMaximo: false };
};

/** Um passo de 0,5 a partir do valor atual, sem erro de ponto flutuante. */
export const passo = (n: number, direcao: 1 | -1, max: number): number =>
  limitarPercentual(Math.round((n + direcao * PASSO_PCT) * 100) / 100, max).valor;

type Props = {
  id: string;
  label: string;
  valor: number;
  max: number;
  onChange: (valor: number) => void;
  /** O que vai embaixo do campo (valor em R$, arredondamento). */
  children?: React.ReactNode;
};

export const PercentStepper = ({ id, label, valor, max, onChange, children }: Props) => {
  const [texto, setTexto] = useState(formatarPercentual(valor));
  const [focado, setFocado] = useState(false);
  const [avisoMaximo, setAvisoMaximo] = useState(false);

  // Valor mudado por fora (ex.: o ajuste que mantém livre + embutido ≤ 100%):
  // o campo acompanha, menos enquanto a pessoa digita.
  useEffect(() => {
    if (!focado) setTexto(formatarPercentual(valor));
  }, [valor, focado]);

  const aplicar = (n: number) => {
    const { valor: v, acimaDoMaximo } = limitarPercentual(n, max);
    setAvisoMaximo(acimaDoMaximo);
    onChange(v);
    return v;
  };

  const aoDigitar = (novo: string) => {
    setTexto(novo);
    const n = lerPercentual(novo);
    if (n == null) return; // vazio no meio da digitação: decide no blur
    const v = aplicar(n);
    if (v !== n) setTexto(formatarPercentual(v));
  };

  const aoSair = () => {
    setFocado(false);
    const n = lerPercentual(texto);
    const v = aplicar(n ?? 0);
    setTexto(formatarPercentual(v));
  };

  const clicar = (direcao: 1 | -1) => {
    const v = aplicar(passo(valor, direcao, max));
    setTexto(formatarPercentual(v));
  };

  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`Diminuir ${label}`}
          disabled={valor <= 0}
          onClick={() => clicar(-1)}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <div className="relative flex-1">
          <Input
            id={id}
            inputMode="decimal"
            value={texto}
            onFocus={() => setFocado(true)}
            onChange={(e) => aoDigitar(e.target.value)}
            onBlur={aoSair}
            className="pr-8 text-center text-base font-semibold"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            %
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`Aumentar ${label}`}
          disabled={valor >= max}
          onClick={() => clicar(1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {avisoMaximo && (
        <p role="alert" className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
          O máximo permitido é {formatarPercentual(max)}%.
        </p>
      )}
      {children}
    </div>
  );
};
